import { createTranslator, type Translator } from "@veycue/i18n";
import { isLocale, type Locale } from "@veycue/shared";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { api, refreshSession, setAccessToken } from "../api/client";
import type { AuthResponse, MeResponse, OrganizationSummary, SessionUser } from "../api/types";

interface SessionState {
  readonly status: "loading" | "authenticated" | "anonymous";
  readonly user: SessionUser | null;
  readonly organizations: readonly OrganizationSummary[];
  readonly organization: OrganizationSummary | null;
  readonly translator: Translator;
  signIn(email: string, password: string): Promise<void>;
  signUp(email: string, password: string, name?: string): Promise<void>;
  signOut(): Promise<void>;
  selectOrganization(id: string): void;
  reload(): Promise<void>;
}

const SessionContext = createContext<SessionState | null>(null);

const ORGANIZATION_KEY = "veycue.organization";

export function SessionProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<SessionState["status"]>("loading");
  const [user, setUser] = useState<SessionUser | null>(null);
  const [organizations, setOrganizations] = useState<readonly OrganizationSummary[]>([]);
  const [organizationId, setOrganizationId] = useState<string | null>(
    () => localStorage.getItem(ORGANIZATION_KEY),
  );

  const load = useCallback(async () => {
    try {
      const me = await api<MeResponse>("/v1/auth/me");
      setUser(me.user);
      setOrganizations(me.organizations);
      setStatus("authenticated");
    } catch {
      setUser(null);
      setOrganizations([]);
      setStatus("anonymous");
    }
  }, []);

  useEffect(() => {
    // Po odświeżeniu strony access token przepadł (żyje w pamięci), ale cookie
    // odświeżania zostało — dlatego sesję odtwarzamy, zamiast wyrzucać na login.
    void (async () => {
      const restored = await refreshSession();
      if (!restored) {
        // Bez ważnego cookie nie ma po co pytać o /me: to gwarantowane 401,
        // które pociągnęłoby za sobą kolejną próbę odświeżenia.
        setStatus("anonymous");
        return;
      }
      await load();
    })();
  }, [load]);

  const authenticate = useCallback(
    async (path: string, body: Record<string, unknown>) => {
      const result = await api<AuthResponse>(path, { method: "POST", body, skipRefresh: true });
      setAccessToken(result.accessToken);
      await load();
    },
    [load],
  );

  const value = useMemo<SessionState>(() => {
    const organization =
      organizations.find((org) => org.id === organizationId) ?? organizations[0] ?? null;

    const locale: Locale = user && isLocale(user.locale) ? user.locale : "pl";

    return {
      status,
      user,
      organizations,
      organization,
      translator: createTranslator(locale),
      signIn: (email, password) => authenticate("/v1/auth/login", { email, password }),
      signUp: (email, password, name) =>
        authenticate("/v1/auth/register", { email, password, ...(name ? { name } : {}) }),
      signOut: async () => {
        await api<void>("/v1/auth/logout", { method: "POST" }).catch(() => undefined);
        setAccessToken(null);
        setUser(null);
        setOrganizations([]);
        setStatus("anonymous");
      },
      selectOrganization: (id) => {
        localStorage.setItem(ORGANIZATION_KEY, id);
        setOrganizationId(id);
      },
      reload: load,
    };
  }, [authenticate, load, organizationId, organizations, status, user]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionState {
  const context = useContext(SessionContext);
  if (!context) throw new Error("useSession poza SessionProvider");
  return context;
}

/** Skrót do tłumaczeń — komponenty nigdy nie zawierają tekstów wprost (§46). */
export function useT() {
  return useSession().translator;
}
